


















import Vue, { PropType } from 'vue';
import {
  ComputationBinaryOperators,
  ComputationNodeType,
  OperatorNodeType,
  ServiceComputationNode,
  ServiceComputationNodeFieldError
} from '@/models';

type IOperatorList = {
  text: string;
  value: ComputationBinaryOperators | null;
}[];

import mixins from '@/utils/mixins';
import { inject as RegistrableInject } from '@/mixins/registrable';
import BindsAttrs from '@/mixins/binds-attrs';
const baseMixins = mixins(
  BindsAttrs,
  RegistrableInject<'formExpression', any>('formExpression')
);
type ErrorBag = Record<number, boolean>;
interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    form: Vue & {
      validate: () => boolean;
      resetValidation: () => void;
      reset: () => void;
    };
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'ComputationOperatorNode',

    props: {
      value: {
        required: false,
        type: Object as PropType<ServiceComputationNode>,
        default: () =>
          new ServiceComputationNode({
            type: ComputationNodeType.operand,
            subtype: ComputationBinaryOperators.add
          })
      },

      fieldError: {
        required: false,
        type: Object as PropType<ServiceComputationNodeFieldError>,
        default: () => new ServiceComputationNodeFieldError()
      },

      disabled: {
        type: Boolean,
        default: false
      },

      isFirst: {
        type: Boolean,
        default: false
      },

      type: {
        type: String as PropType<OperatorNodeType>,
        default: 'binary'
      }
    },

    data() {
      return {
        valid: false,
        internalLazyValue: this.value,
        errorBag: {} as ErrorBag
      };
    },

    computed: {
      isBinary(): boolean {
        return this.type == 'binary';
      },

      isUnary(): boolean {
        return this.type == 'unary';
      },

      hasError(): boolean {
        return !this.valid;
      },

      internalValue: {
        get(): ServiceComputationNode {
          return this.internalLazyValue;
        },
        set(val: any) {
          if (val === this.internalLazyValue) return;

          if (this.isFirst || this.isUnary) {
            if (this.value.isUnaryOperator() !== val.isUnaryOperator()) {
              this.$emit('unary:input', val);
              return;
            }
          }

          this.internalLazyValue = val;

          // hack for passing multiple function on the emit values
          this.$emit('input', val);
        }
      },

      nodeOperatorSubtypeList() {
        const binaryOperatorList: IOperatorList = [
          { text: 'Add', value: ComputationBinaryOperators.add },
          { text: 'Subtract', value: ComputationBinaryOperators.subtract },
          { text: 'Multiply', value: ComputationBinaryOperators.multiply },
          { text: 'Divide', value: ComputationBinaryOperators.divide },
          { text: 'Exponent', value: ComputationBinaryOperators.exponent }
        ];

        const bracketOperatorList: IOperatorList = [
          {
            text: 'Bracket Open',
            value: ComputationBinaryOperators.start_bracket
          }
        ];

        const unaryOperatorList: IOperatorList = [
          { text: 'Ceil', value: ComputationBinaryOperators.ceil },
          { text: 'Floor', value: ComputationBinaryOperators.floor },
          { text: 'Abs', value: ComputationBinaryOperators.abs }
        ];

        const operatorSelectList: IOperatorList = [];

        if (this.isFirst) {
          operatorSelectList.push(...bracketOperatorList);
        } else {
          if (this.isUnary) {
            operatorSelectList.push(...unaryOperatorList);
          } else {
            operatorSelectList.push(...binaryOperatorList);
          }
        }
        return operatorSelectList;
      },

      // rules
      operatorRules() {
        return [
          (v: string) => {
            if (this.disabled) return true;
            return v != null || 'An operator is required';
          }
        ];
      }
    },

    watch: {
      value: {
        handler(val) {
          this.internalLazyValue = val;
        },
        deep: true
      }
    },

    methods: {
      setSubtype(subtype?: ComputationBinaryOperators) {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          subtype: subtype
        });
        this.internalValue = newNode;
      },

      /** @public */
      reset() {
        const newNode = new ServiceComputationNode({
          id: this.internalValue.id,
          type: this.internalValue.type,
          order: this.internalValue.order,
          // to clear the subtype will stop form from continuing
          subtype: undefined
        });
        this.internalValue = newNode;
        return this.$refs['form'].reset();
      },

      /** @public */
      resetValidation() {
        return this.$refs['form'].resetValidation();
      },

      validate(): boolean {
        return this.$refs['form'].validate();
      }
    },

    created() {
      this.formExpression && this.formExpression.register(this);
    },

    beforeDestroy() {
      this.formExpression && this.formExpression.unregister(this);
    }
  });
